import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { BarChart } from "@mui/x-charts/BarChart";
import UsageApi, {
  UsageByTicketRow,
  UsageByUserAssistantRow,
  UsageByUserTicketsRow,
  UsageDailyRow,
  UsageFilterParams,
  UsageResponse,
  UsageSummary,
} from "../apis/usageApi";
import type { UsageQuickDatePresetId } from "../apis/usageApiUtils";
import {
  expandDailyUsageForRange,
  getUsageDateRangeForPreset,
  USAGE_DEFAULT_QUICK_PRESET,
  USAGE_QUICK_RANGE_OPTIONS,
} from "../apis/usageApiUtils";
import LayeredAutocompleteSelect from "../components/LayeredAutocompleteSelect";
import { useUserMsp } from "../hooks/useUserMsp";
import { canAccessUsageAnalytics, isHorizonRootRole } from "../utils/horizonRoles";
import { usageActorLabel } from "../utils/usageInsightsUtils";

const DEFAULT_SUMMARY: UsageSummary = {
  actions: 0,
  ticket_actions: 0,
  assistant_actions: 0,
};

const usageInitialDateRange = getUsageDateRangeForPreset(USAGE_DEFAULT_QUICK_PRESET)!;

/** Options for Quick range — same shape as other `LayeredAutocompleteSelect` usages. */
const QUICK_RANGE_SELECT_OPTIONS = USAGE_QUICK_RANGE_OPTIONS.map((o) => ({
  value: o.id,
  label: o.label,
}));

const cardSx = { borderRadius: "8px", border: "1px solid #E2E8F0" };

const sectionTitleSx = {
  fontWeight: 700,
  mb: 2,
  color: "primary.main" as const,
  flexShrink: 0,
};

/**
 * Shared 3-column grid for KPI cards and usage breakdown panels so column widths
 * align (keyword: usage layout grid).
 */
const usageSummaryGridRowSx = {
  display: "grid",
  gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" },
  gap: 2,
  mb: 3,
  width: "100%",
  alignItems: "stretch" as const,
};
const usageSummaryGridCellSx = {
  minWidth: 0,
  display: "flex" as const,
  flexDirection: "column" as const,
  minHeight: 0,
};
const kpiCardSx = {
  ...cardSx,
  width: "100%",
  height: "100%",
  display: "flex",
  flexDirection: "column" as const,
};
const kpiCardContentSx = {
  flex: 1,
  display: "flex",
  flexDirection: "column" as const,
};
const totalActionsChartCardSx = {
  ...cardSx,
  mb: 3,
  width: "100%",
};
const totalActionsChartBoxSx = {
  width: "100%",
  height: 280,
  minHeight: 280,
};

/** Fixed height so all five panels align; tables scroll inside (keyword: usage panel scroll). */
const USAGE_PANEL_HEIGHT_PX = 440;
const panelCardSx = {
  ...cardSx,
  width: "100%",
  height: USAGE_PANEL_HEIGHT_PX,
  minHeight: USAGE_PANEL_HEIGHT_PX,
  maxHeight: USAGE_PANEL_HEIGHT_PX,
  display: "flex",
  flexDirection: "column" as const,
  overflow: "hidden",
};
const panelCardContentSx = {
  flex: 1,
  display: "flex",
  flexDirection: "column" as const,
  width: "100%",
  minHeight: 0,
  overflow: "hidden",
  boxSizing: "border-box" as const,
};
const panelTableContainerSx = {
  flex: 1,
  minHeight: 0,
  width: "100%",
  overflow: "auto" as const,
};

const usagePanelTableSx = {
  width: "100%",
  tableLayout: "fixed" as const,
  "& tbody .MuiTableCell-root": { bgcolor: "transparent" },
};

function usageActionsShareRowSx(theme: Theme, rowActions: number, panelTotal: number) {
  if (panelTotal <= 0 || !Number.isFinite(rowActions) || rowActions <= 0) {
    return {};
  }
  const share = Math.min(1, rowActions / panelTotal);
  const emptyPct = (1 - share) * 100;
  const fill = alpha(theme.palette.primary.main, 0.12);
  return {
    backgroundImage: `linear-gradient(to right, transparent 0%, transparent ${emptyPct}%, ${fill} ${emptyPct}%, ${fill} 100%)`,
    backgroundRepeat: "no-repeat" as const,
  };
}

function summaryFromApi(s: UsageSummary | undefined): UsageSummary {
  if (!s) return DEFAULT_SUMMARY;
  return {
    actions: Number(s.actions) || 0,
    ticket_actions: Number(s.ticket_actions) || 0,
    assistant_actions: Number(s.assistant_actions) || 0,
  };
}

function applyUsageResponse(
  response: UsageResponse,
  setters: {
    setSummary: (s: UsageSummary) => void;
    setUsageByUserTickets: (r: UsageByUserTicketsRow[]) => void;
    setUsageByUserAssistant: (r: UsageByUserAssistantRow[]) => void;
    setUsageByTicket: (r: UsageByTicketRow[]) => void;
    setDailyTickets: (r: UsageDailyRow[]) => void;
    setDailyAssistant: (r: UsageDailyRow[]) => void;
    setAvailableUsers: (r: string[]) => void;
  }
) {
  setters.setSummary(summaryFromApi(response.summary));
  setters.setUsageByUserTickets(
    Array.isArray(response.usage_by_user_tickets) ? response.usage_by_user_tickets : []
  );
  setters.setUsageByUserAssistant(
    Array.isArray(response.usage_by_user_assistant) ? response.usage_by_user_assistant : []
  );
  setters.setUsageByTicket(Array.isArray(response.usage_by_ticket) ? response.usage_by_ticket : []);
  setters.setDailyTickets(Array.isArray(response.daily_tickets) ? response.daily_tickets : []);
  setters.setDailyAssistant(Array.isArray(response.daily_assistant) ? response.daily_assistant : []);
  setters.setAvailableUsers(Array.isArray(response.available_users) ? response.available_users : []);
}

type UsageLoadRequest = {
  key: string;
  params: UsageFilterParams;
};

function usageLoadRequestKey(params: UsageFilterParams): string {
  return JSON.stringify({
    startDate: params.startDate ?? null,
    endDate: params.endDate ?? null,
    userEmail: params.userEmail ? String(params.userEmail).trim().toLowerCase() : null,
    limit: params.limit ?? null,
    includeRootUsers: params.includeRootUsers === true,
    mspId: params.mspId ?? null,
  });
}

/** Usage analytics for MSP administrators; keyword: usage quick date (preset dropdown). */
const Usage: React.FC = () => {
  const theme = useTheme();
  const { userMspInfo, isLoading: userMspLoading, error: userMspError } = useUserMsp();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userListError, setUserListError] = useState<string | null>(null);
  const [summary, setSummary] = useState<UsageSummary>(DEFAULT_SUMMARY);
  const [usageByUserTickets, setUsageByUserTickets] = useState<UsageByUserTicketsRow[]>([]);
  const [usageByUserAssistant, setUsageByUserAssistant] = useState<UsageByUserAssistantRow[]>([]);
  const [usageByTicket, setUsageByTicket] = useState<UsageByTicketRow[]>([]);
  const [dailyTickets, setDailyTickets] = useState<UsageDailyRow[]>([]);
  const [dailyAssistant, setDailyAssistant] = useState<UsageDailyRow[]>([]);
  const [availableUsers, setAvailableUsers] = useState<string[]>([]);
  const [mspUserEmails, setMspUserEmails] = useState<string[]>([]);

  const [quickPreset, setQuickPreset] = useState<UsageQuickDatePresetId>(USAGE_DEFAULT_QUICK_PRESET);
  const [startDate, setStartDate] = useState<string>(usageInitialDateRange.startDate);
  const [endDate, setEndDate] = useState<string>(usageInitialDateRange.endDate);
  const [selectedUser, setSelectedUser] = useState<string>("all");
  const activeUsageRequestKeyRef = useRef<string | null>(null);
  const queuedUsageRequestRef = useRef<UsageLoadRequest | null>(null);

  const canViewUsage = canAccessUsageAnalytics(userMspInfo?.role);
  const isRootViewer = isHorizonRootRole(userMspInfo?.role);
  const includeRootUsers = isRootViewer && selectedUser === "include_root_users";

  /** Supervisor ticket URLs are MSP-scoped; usage now always stays on the viewer's own MSP. */
  const canLinkUsageTicketIds = useMemo(() => {
    return userMspInfo?.msp_id != null;
  }, [userMspInfo?.msp_id]);

  const runUsageRequest = useCallback(async (initialRequest: UsageLoadRequest) => {
    let request: UsageLoadRequest | null = initialRequest;
    while (request) {
      if (activeUsageRequestKeyRef.current) {
        queuedUsageRequestRef.current = request;
        return;
      }

      activeUsageRequestKeyRef.current = request.key;
      setLoading(true);
      setError(null);
      try {
        const response = await UsageApi.getUsage(request.params);

        applyUsageResponse(response, {
          setSummary,
          setUsageByUserTickets,
          setUsageByUserAssistant,
          setUsageByTicket,
          setDailyTickets,
          setDailyAssistant,
          setAvailableUsers,
        });
      } catch (err: any) {
        setError(err?.userMessage || err?.message || "Failed to load usage data");
      } finally {
        if (activeUsageRequestKeyRef.current === request.key) {
          activeUsageRequestKeyRef.current = null;
        }
      }

      const queued = queuedUsageRequestRef.current;
      queuedUsageRequestRef.current = null;
      if (queued && queued.key !== request.key) {
        request = queued;
        continue;
      }
      request = null;
    }
    setLoading(false);
  }, []);

  const loadUsage = useCallback(async () => {
    if (!canViewUsage) return;
    const params: UsageFilterParams = {
      startDate: startDate || null,
      endDate: endDate || null,
      userEmail: selectedUser === "all" || selectedUser === "include_root_users" ? null : selectedUser,
      limit: 200,
      includeRootUsers,
      mspId: null,
    };
    await runUsageRequest({
      key: usageLoadRequestKey(params),
      params,
    });
  }, [
    canViewUsage,
    startDate,
    endDate,
    selectedUser,
    includeRootUsers,
    runUsageRequest,
  ]);

  const loadMspUserEmails = async () => {
    if (!canViewUsage) return;
    try {
      setUserListError(null);
      const emails = await UsageApi.getMspUserEmails({
        includeRootUsers,
        mspId: null,
      });
      setMspUserEmails(emails);
    } catch (err: any) {
      setUserListError(err?.userMessage || err?.message || "Failed to load MSP user list");
      setMspUserEmails([]);
    }
  };

  useEffect(() => {
    if (!isRootViewer && selectedUser === "include_root_users") {
      setSelectedUser("all");
    }
  }, [isRootViewer, selectedUser]);

  useEffect(() => {
    if (!userMspLoading && canViewUsage) {
      void loadMspUserEmails();
    }
  }, [userMspLoading, canViewUsage, includeRootUsers, isRootViewer]);

  /** Refetch when filters change or when admin context becomes ready (keyword: usage auto refresh). */
  useEffect(() => {
    if (userMspLoading || !canViewUsage) return;
    void loadUsage();
  }, [userMspLoading, canViewUsage, loadUsage]);

  useEffect(() => {
    document.title = "Usage - Theo";
    return () => {
      document.title = "Theo";
    };
  }, []);

  const userFilterEmails = useMemo(() => {
    const emails = new Set<string>();
    for (const e of mspUserEmails) {
      if (typeof e === "string" && e.trim() !== "") emails.add(e.trim());
    }
    for (const e of availableUsers) {
      if (typeof e === "string" && e.trim() !== "") emails.add(e.trim());
    }
    return Array.from(emails).sort((a, b) => a.localeCompare(b));
  }, [mspUserEmails, availableUsers]);

  useEffect(() => {
    if (selectedUser === "all" || selectedUser === "include_root_users") return;
    if (userFilterEmails.length > 0 && !userFilterEmails.includes(selectedUser)) {
      setSelectedUser("all");
    }
  }, [selectedUser, userFilterEmails]);

  /** Continuous calendar days for “by day” tables; gaps show 0 actions (keyword: usage daily fill). */
  const dailyTicketsDisplay = useMemo(
    () => expandDailyUsageForRange(startDate, endDate, dailyTickets, "desc"),
    [startDate, endDate, dailyTickets]
  );
  const dailyAssistantDisplay = useMemo(
    () => expandDailyUsageForRange(startDate, endDate, dailyAssistant, "desc"),
    [startDate, endDate, dailyAssistant]
  );
  const totalActionsByDayChartData = useMemo(() => {
    const ticketRows = expandDailyUsageForRange(startDate, endDate, dailyTickets, "asc");
    const assistantRows = expandDailyUsageForRange(startDate, endDate, dailyAssistant, "asc");
    const assistantByDate = new Map(
      assistantRows.map((row) => [row.date || "", Number(row.actions) || 0])
    );

    return ticketRows.map((row) => {
      const date = row.date || "";
      return {
        date,
        actions: (Number(row.actions) || 0) + (assistantByDate.get(date) || 0),
      };
    });
  }, [startDate, endDate, dailyTickets, dailyAssistant]);

  const panelTotals = useMemo(
    () => ({
      userTickets: usageByUserTickets.reduce((s, r) => s + r.actions, 0),
      userAssistant: usageByUserAssistant.reduce((s, r) => s + r.actions, 0),
      ticket: usageByTicket.reduce((s, r) => s + r.actions, 0),
      dayTickets: dailyTicketsDisplay.reduce((s, r) => s + r.actions, 0),
      dayAssistant: dailyAssistantDisplay.reduce((s, r) => s + r.actions, 0),
    }),
    [
      usageByUserTickets,
      usageByUserAssistant,
      usageByTicket,
      dailyTicketsDisplay,
      dailyAssistantDisplay,
    ]
  );

  if (userMspLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (userMspError || !userMspInfo) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
        <Alert severity="error">
          {userMspError || "Unable to load your MSP profile. Try refreshing the page."}
        </Alert>
      </Box>
    );
  }

  if (!canViewUsage) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
        <Alert severity="info">
          Usage is only available to MSP administrators. The Usage link is hidden from your sidebar.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
      {error && (
        <Alert sx={{ mb: 3 }} severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {userListError && (
        <Alert sx={{ mb: 3 }} severity="warning" onClose={() => setUserListError(null)}>
          {userListError}
        </Alert>
      )}

      <Card sx={{ ...cardSx, mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={3}>
              <TextField
                label="Start Date"
                type="date"
                size="small"
                value={startDate}
                onChange={(event) => {
                  setQuickPreset("custom");
                  setStartDate(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="End Date"
                type="date"
                size="small"
                value={endDate}
                onChange={(event) => {
                  setQuickPreset("custom");
                  setEndDate(event.target.value);
                }}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <LayeredAutocompleteSelect
                label="Quick range"
                size="small"
                value={quickPreset}
                disableClearable
                options={QUICK_RANGE_SELECT_OPTIONS}
                onChange={(next) => {
                  const id = next as UsageQuickDatePresetId;
                  setQuickPreset(id);
                  const range = getUsageDateRangeForPreset(id);
                  if (range) {
                    setStartDate(range.startDate);
                    setEndDate(range.endDate);
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <LayeredAutocompleteSelect
                label="User"
                size="small"
                value={selectedUser}
                onChange={setSelectedUser}
                disableClearable
                options={[
                  { value: "all", label: "All users" },
                  ...(isRootViewer ? [{ value: "include_root_users", label: "Include Theo staff" }] : []),
                  ...userFilterEmails.map((email) => ({
                    value: email,
                    label: email,
                  })),
                ]}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Box sx={usageSummaryGridRowSx}>
            <Box sx={usageSummaryGridCellSx}>
              <Card sx={kpiCardSx}>
                <CardContent sx={kpiCardContentSx}>
                  <Typography variant="body2" color="text.secondary">
                    Total actions
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {summary.actions}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={usageSummaryGridCellSx}>
              <Card sx={kpiCardSx}>
                <CardContent sx={kpiCardContentSx}>
                  <Typography variant="body2" color="text.secondary">
                    Ticket actions
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {summary.ticket_actions}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            <Box sx={usageSummaryGridCellSx}>
              <Card sx={kpiCardSx}>
                <CardContent sx={kpiCardContentSx}>
                  <Typography variant="body2" color="text.secondary">
                    Assistant actions
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>
                    {summary.assistant_actions}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          <Card sx={totalActionsChartCardSx}>
            <CardContent>
              <Typography variant="h6" sx={sectionTitleSx}>
                Total Actions over time
              </Typography>
              {totalActionsByDayChartData.length > 0 ? (
                <Box sx={totalActionsChartBoxSx}>
                  <BarChart
                    xAxis={[
                      {
                        scaleType: "band",
                        data: totalActionsByDayChartData.map((item) => item.date),
                        label: "Date",
                      },
                    ]}
                    yAxis={[
                      {
                        label: "Actions",
                      },
                    ]}
                    series={[
                      {
                        data: totalActionsByDayChartData.map((item) => item.actions),
                        label: "Total actions",
                        color: theme.palette.primary.main,
                      },
                    ]}
                    height={280}
                    margin={{ left: 56, right: 24, top: 24, bottom: 56 }}
                    slotProps={{
                      legend: { hidden: true },
                      bar: {
                        rx: 4,
                        ry: 4,
                      },
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No actions for this range.
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Breakdown grid: row1 = by day (tickets), by day (assistant), by ticket; row2 = by user (tickets), by user (assistant) — keyword: usage panel order */}
          <Box sx={usageSummaryGridRowSx}>
            <Box sx={usageSummaryGridCellSx}>
              <Card sx={panelCardSx}>
                <CardContent sx={panelCardContentSx}>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    Actions on Tickets by day
                  </Typography>
                  <TableContainer sx={panelTableContainerSx}>
                    <Table size="small" stickyHeader sx={usagePanelTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: "55%" }}>Date</TableCell>
                          <TableCell align="right" sx={{ width: "45%" }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dailyTicketsDisplay.map((item) => (
                          <TableRow
                            key={`dt-${item.date}`}
                            sx={usageActionsShareRowSx(theme, item.actions, panelTotals.dayTickets)}
                          >
                            <TableCell>{item.date || "-"}</TableCell>
                            <TableCell align="right">{item.actions}</TableCell>
                          </TableRow>
                        ))}
                        {dailyTicketsDisplay.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} align="center">No per-day ticket usage.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>

            <Box sx={usageSummaryGridCellSx}>
              <Card sx={panelCardSx}>
                <CardContent sx={panelCardContentSx}>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    Actions on Chat Agent by day
                  </Typography>
                  <TableContainer sx={panelTableContainerSx}>
                    <Table size="small" stickyHeader sx={usagePanelTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: "55%" }}>Date</TableCell>
                          <TableCell align="right" sx={{ width: "45%" }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {dailyAssistantDisplay.map((item) => (
                          <TableRow
                            key={`da-${item.date}`}
                            sx={usageActionsShareRowSx(theme, item.actions, panelTotals.dayAssistant)}
                          >
                            <TableCell>{item.date || "-"}</TableCell>
                            <TableCell align="right">{item.actions}</TableCell>
                          </TableRow>
                        ))}
                        {dailyAssistantDisplay.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} align="center">No per-day assistant usage.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>

            <Box sx={usageSummaryGridCellSx}>
              <Card sx={panelCardSx}>
                <CardContent sx={panelCardContentSx}>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    Tickets with most actions
                  </Typography>
                  {/* Ticket column: deep-links to supervisor only when usage MSP matches viewer MSP (keyword: usage ticket link msp). */}
                  <TableContainer sx={panelTableContainerSx}>
                    <Table size="small" stickyHeader sx={usagePanelTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: "55%" }}>Ticket</TableCell>
                          <TableCell align="right" sx={{ width: "45%" }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usageByTicket.map((t) => {
                          const ticketIdStr =
                            t.ticket_id != null && String(t.ticket_id).trim() !== ""
                              ? String(t.ticket_id).trim()
                              : "";
                          return (
                            <TableRow
                              key={t.ticket_id ?? ticketIdStr}
                              sx={usageActionsShareRowSx(theme, t.actions, panelTotals.ticket)}
                            >
                              <TableCell sx={{ wordBreak: "break-word" }}>
                                {ticketIdStr ? (
                                  canLinkUsageTicketIds ? (
                                    <Link
                                      href={`/supervisor/tickets/${encodeURIComponent(ticketIdStr)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      underline="hover"
                                      sx={{ fontWeight: 600 }}
                                    >
                                      {ticketIdStr}
                                    </Link>
                                  ) : (
                                    // Plain id when viewer MSP != filtered MSP — normal weight (links stay semibold).
                                    <span>{ticketIdStr}</span>
                                  )
                                ) : (
                                  "-"
                                )}
                              </TableCell>
                              <TableCell align="right">{t.actions}</TableCell>
                            </TableRow>
                          );
                        })}
                        {usageByTicket.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} align="center">No ticket usage for this range.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>

            <Box sx={usageSummaryGridCellSx}>
              <Card sx={panelCardSx}>
                <CardContent sx={panelCardContentSx}>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    Actions on Tickets by user
                  </Typography>
                  <TableContainer sx={panelTableContainerSx}>
                    <Table size="small" stickyHeader sx={usagePanelTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: "48%" }}>User</TableCell>
                          <TableCell align="right" sx={{ width: "26%" }}>Tickets</TableCell>
                          <TableCell align="right" sx={{ width: "26%" }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usageByUserTickets.map((u) => (
                          <TableRow
                            key={u.user_email ?? "unknown"}
                            sx={usageActionsShareRowSx(theme, u.actions, panelTotals.userTickets)}
                          >
                            <TableCell sx={{ wordBreak: "break-word" }}>{usageActorLabel(u.user_email)}</TableCell>
                            <TableCell align="right">{u.ticket_count}</TableCell>
                            <TableCell align="right">{u.actions}</TableCell>
                          </TableRow>
                        ))}
                        {usageByUserTickets.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={3} align="center">No ticket-scoped usage for this range.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>

            <Box sx={usageSummaryGridCellSx}>
              <Card sx={panelCardSx}>
                <CardContent sx={panelCardContentSx}>
                  <Typography variant="h6" sx={sectionTitleSx}>
                    Actions on Chat Agent by user
                  </Typography>
                  <TableContainer sx={panelTableContainerSx}>
                    <Table size="small" stickyHeader sx={usagePanelTableSx}>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: "55%" }}>User</TableCell>
                          <TableCell align="right" sx={{ width: "45%" }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {usageByUserAssistant.map((u) => (
                          <TableRow
                            key={`assistant-${u.user_email ?? "unknown"}`}
                            sx={usageActionsShareRowSx(theme, u.actions, panelTotals.userAssistant)}
                          >
                            <TableCell sx={{ wordBreak: "break-word" }}>{usageActorLabel(u.user_email)}</TableCell>
                            <TableCell align="right">{u.actions}</TableCell>
                          </TableRow>
                        ))}
                        {usageByUserAssistant.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={2} align="center">No assistant usage for this range.</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Usage;
